import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import {
  createTicket,
  isApiError,
  listCategories,
  supportDuplicateCandidateId,
  supportErrorCode,
} from "../../lib/support-api";
import type { Category, TicketChannel, TicketPriority } from "../../lib/support-types";
import type { TranslationKey } from "../../lib/i18n";
import { useConfig } from "../../state/config-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  support_duplicate: "support.error.duplicate",
  invalid_state_transition: "support.error.invalidTransition",
  support_ticket_not_found: "support.error.notFound",
};

function SupportTicketFormInner(): ReactNode {
  const { t } = useConfig();
  const navigate = useNavigate();

  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("medium");
  const [channel, setChannel] = useState<TicketChannel>("portal");
  const [categoryId, setCategoryId] = useState("");
  const [requesterName, setRequesterName] = useState("");
  const [requesterEmail, setRequesterEmail] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const page = await listCategories();
        if (!cancelled) {
          setCategories(page.items);
        }
      } catch {
        if (!cancelled) {
          setCategories([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setSaving(true);
      setError(null);
      setDuplicateId(null);
      try {
        const ticket = await createTicket({
          subject: subject.trim(),
          description: description.trim(),
          priority,
          channel,
          category_id: categoryId === "" ? null : categoryId,
          requester_name: requesterName.trim() === "" ? null : requesterName.trim(),
          requester_email: requesterEmail.trim() === "" ? null : requesterEmail.trim(),
        });
        navigate(`/support/tickets/${ticket.id}`, { replace: true });
      } catch (caught) {
        const candidate = supportDuplicateCandidateId(caught);
        if (candidate !== null) {
          setDuplicateId(candidate);
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        } else {
          const code = supportErrorCode(caught);
          const key = code === null ? undefined : ERROR_KEYS[code];
          setError(
            key !== undefined ? t(key) : isApiError(caught) ? caught.detail : t("error.network"),
          );
        }
      } finally {
        setSaving(false);
      }
    },
    [categoryId, channel, description, navigate, priority, requesterEmail, requesterName, subject, t],
  );

  return (
    <section className="page" aria-labelledby="support-ticket-form-title">
      <header className="page__header">
        <h1 id="support-ticket-form-title">{t("support.form.createTitle")}</h1>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {duplicateId !== null ? (
        <p>
          <Link to={`/support/tickets/${duplicateId}`}>{t("support.common.view")}</Link>
        </p>
      ) : null}

      <form className="form form--inline-card" onSubmit={(event) => void onSubmit(event)}>
        <div className="form-grid">
          <label className="field" htmlFor="ticket-subject">
            <span>{t("support.ticket.subject")}</span>
            <input
              id="ticket-subject"
              value={subject}
              maxLength={200}
              required
              aria-describedby="ticket-subject-hint"
              onChange={(event) => setSubject(event.target.value)}
            />
            <small id="ticket-subject-hint" className="field__hint">
              {t("support.form.subjectHint")}
            </small>
          </label>
          <label className="field" htmlFor="ticket-priority">
            <span>{t("support.ticket.priority")}</span>
            <select
              id="ticket-priority"
              value={priority}
              onChange={(event) => setPriority(event.target.value as TicketPriority)}
            >
              <option value="low">{t("support.priority.low")}</option>
              <option value="medium">{t("support.priority.medium")}</option>
              <option value="high">{t("support.priority.high")}</option>
              <option value="urgent">{t("support.priority.urgent")}</option>
            </select>
          </label>
          <label className="field" htmlFor="ticket-channel">
            <span>{t("support.ticket.channel")}</span>
            <select
              id="ticket-channel"
              value={channel}
              onChange={(event) => setChannel(event.target.value as TicketChannel)}
            >
              <option value="portal">{t("support.channel.portal")}</option>
              <option value="email">{t("support.channel.email")}</option>
              <option value="chat">{t("support.channel.chat")}</option>
              <option value="phone">{t("support.channel.phone")}</option>
              <option value="api">{t("support.channel.api")}</option>
            </select>
          </label>
          <label className="field" htmlFor="ticket-category">
            <span>{t("support.ticket.category")}</span>
            <select
              id="ticket-category"
              value={categoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              <option value="">{t("support.ticket.categoryNone")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field" htmlFor="ticket-requester-name">
            <span>{t("support.ticket.requesterName")}</span>
            <input
              id="ticket-requester-name"
              value={requesterName}
              maxLength={200}
              onChange={(event) => setRequesterName(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="ticket-requester-email">
            <span>{t("support.ticket.requesterEmail")}</span>
            <input
              id="ticket-requester-email"
              type="email"
              value={requesterEmail}
              maxLength={320}
              onChange={(event) => setRequesterEmail(event.target.value)}
            />
          </label>
          <label className="field" htmlFor="ticket-description">
            <span>{t("support.ticket.description")}</span>
            <textarea
              id="ticket-description"
              value={description}
              rows={6}
              required
              aria-describedby="ticket-description-hint"
              onChange={(event) => setDescription(event.target.value)}
            />
            <small id="ticket-description-hint" className="field__hint">
              {t("support.form.descriptionHint")}
            </small>
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={saving}>
            {saving ? t("support.form.submitting") : t("support.form.submit")}
          </button>
          <Link className="btn btn--ghost" to="/support/tickets">
            {t("common.cancel")}
          </Link>
        </div>
      </form>
    </section>
  );
}

export function SupportTicketFormPage(): ReactNode {
  return (
    <RequirePermission permission="support.ticket.create">
      <SupportTicketFormInner />
    </RequirePermission>
  );
}